import classNames from 'classnames';
import L, { Map as LeafletMap, type LatLngTuple, type MarkerCluster } from 'leaflet';
import MiniMap from 'leaflet-minimap';
import 'leaflet-minimap/dist/Control.MiniMap.min.css';
import 'leaflet.markercluster';
import 'leaflet/dist/leaflet.css';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { LayerGroup, MapContainer, TileLayer, ZoomControl } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { ReactComponent as FullscreenClose } from 'src/assets/map/fullscreen-close.svg';
import { ReactComponent as FullscreenOpen } from 'src/assets/map/fullscreen-open.svg';
import PageHeader from 'src/components/page-header/page-header';
import { useSearchParams } from 'react-router-dom';
import css from './business-map.module.scss';
import MapFilters from './filters';
import Marker from './marker';
import { type BusinessMapFilters, type BusinessMapParams, type GroupedByCategory } from './types';
import PointCard from './point-card';
import MapCategoryFilter from './category-filter';
import { useResize } from 'src/hooks/shared-hooks';
import PaginatedFooter from 'src/components/paginated-footer/paginated-footer';
import { useGetCategories, useGetMap } from './data/queries';

export default function BusinessMap() {
  const [fullscreen, setFullscreen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchValue = searchParams?.get('search') ?? undefined;
  const [selectedCategory, setSelectedCategory] = useState<string>('-1');

  const { data: categories = [] } = useGetCategories();

  const dimensions = useResize();
  const tabletMode = dimensions.width < 1000;
  const [showCards, setShowCards] = useState(!tabletMode);
  const [searchData, setSearchData] = useState<BusinessMapFilters>({
    filters: {
      search: searchValue
    }
  });

  const [page, setPage] = useState(1);

  const { data: mapData = [] } = useGetMap(searchData.filters);

  const groupedMapData = mapData.reduce<GroupedByCategory>((rv, x) => {
    rv[x.categoryId] = rv[x.categoryId] || [];
    rv[x.categoryId].push(x);
    return rv;
  }, {} as GroupedByCategory);

  useEffect(() => {
    return () => {
      const overlay = document.getElementById('complaint-overlay');
      if (overlay) {
        overlay.remove();
      }
    };
  }, [searchData]);

  const westBound: LatLngTuple = [48.468301, 19.622063];
  const northBound: LatLngTuple = [60.361835, 36.290689];
  const eastBound: LatLngTuple = [49.247834, 43.507104];
  const southBound: LatLngTuple = [41.817795, 32.486602];
  const center: LatLngTuple = [48.383022, 31.1828699];

  const tileUrl = 'https://tiles.openstreetmap.org.ua/osm/{z}/{x}/{y}.png';
  const attribution =
    'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://opendatacommons.org/licenses/odbl/">ODbL</a>';

  const mapRef: MutableRefObject<LeafletMap | null> = useRef<LeafletMap>(null);

  const setCategoryFilter = (id: string) => setSelectedCategory(id);

  const setRef = (node: LeafletMap | null) => {
    if (mapRef.current) {
      mapRef.current.invalidateSize();
    }

    if (!mapRef.current && node) {
      mapRef.current = node;

      const layer = new L.TileLayer(tileUrl, {
        minZoom: 0,
        maxZoom: 13,
        attribution: attribution
      });
      const miniMap = new MiniMap(layer, { toggleDisplay: true });
      miniMap.addTo(node);
    }
  };

  function handleFiltersChange(filters: BusinessMapParams) {
    const nextSearchParams = new URLSearchParams(searchParams);
    filters.search ? nextSearchParams.set('search', filters.search.trim()) : nextSearchParams.delete('search');
    if (nextSearchParams.toString() !== searchParams.toString()) setSearchParams(nextSearchParams);
    setSearchData({ ...searchData, filters: { ...searchData.filters, ...filters } });
  }

  function onFullScreen() {
    setFullscreen((fullscreen) => !fullscreen);
    window.scrollTo(0, 0);
  }
  const mappedData = [...new Map(mapData.map((item) => [item['title'], item])).values()];

  const cards = mappedData.filter((i) => selectedCategory === i.categoryId || selectedCategory === '-1');

  const сardsToShow = cards.slice(0, page * 6);

  const handleCardClick = (title: string) => {
    handleFiltersChange({ search: title });
    setPage(1);
  };

  const showPaginator = cards?.length > 0 && (showCards || !tabletMode) && сardsToShow.length !== cards.length;

  const filter = (
    <div className={css.filters}>
      <MapCategoryFilter
        categories={categories}
        selectedCategory={selectedCategory}
        onFilter={setCategoryFilter}
        isMobile={tabletMode}
        showCards={showCards}
        switchView={() => setShowCards(!showCards)}
      />
      {tabletMode && <MapFilters initialFilters={searchData.filters} onSearch={handleFiltersChange} />}
    </div>
  );
  return (
    <main className={classNames(css.businessMap, fullscreen ? css.fullscreen : '')}>
      {!fullscreen && (
        <PageHeader
          title='Цифрова мапа знижок'
          description='На цій мапі зібрані бізнеси які надають послуги або сервіси зі знижкою або безоплатно для захисників'
          pages={[]}
          subFilter={filter}
        />
      )}
      <div className={css.container}>
        {!fullscreen && (
          <div className={css.cards}>
            {!tabletMode && <MapFilters initialFilters={searchData.filters} onSearch={handleFiltersChange} />}
            {(showCards || !tabletMode) && groupedMapData && (
              <div className={css.cardsContainer}>
                {сardsToShow?.map((item) => (
                  <PointCard
                    key={item.title}
                    data={item}
                    category={categories.find((i) => i.id === item.categoryId)!}
                    filterByCompany={handleCardClick}
                    filter={!searchData.filters.search}
                  />
                ))}
              </div>
            )}
            {showPaginator && (
              <div className={css.pagerRow}>
                <PaginatedFooter caption='Показати ще' onClick={() => setPage(page + 1)} />
              </div>
            )}
          </div>
        )}

        <div className={showCards && tabletMode ? css.hidden : ''}>
          <MapContainer
            ref={setRef}
            center={center}
            zoom={6}
            minZoom={6}
            maxZoom={18}
            maxBounds={[westBound, northBound, eastBound, southBound]}
            scrollWheelZoom
            zoomControl={false}
            className={css.map}>
            <TileLayer attribution={attribution} url={tileUrl} />

            {Object.keys(groupedMapData).map((key) => {
              const category = categories.find((c) => c.id === key);
              return (
                category &&
                (selectedCategory === category.id || selectedCategory === '-1') && (
                  <LayerGroup key={key}>
                    <MarkerClusterGroup
                      chunkedLoading
                      showCoverageOnHover={false}
                      removeOutsideVisibleBounds
                      iconCreateFunction={(cluster: MarkerCluster) => {
                        const childCount = cluster.getChildCount();
                        let c = ' marker-cluster-';
                        if (childCount < 10) {
                          c += 'small';
                        } else if (childCount < 100) {
                          c += 'medium';
                        } else {
                          c += 'large';
                        }

                        return new L.DivIcon({
                          html: `<div style="background-color:${category.color}; box-shadow: 0px 0px 1px 4px ${category.color}80;"><span style="background-color:${category.color};">${childCount}</span></div>`,
                          className: `category  marker-cluster${c}`,
                          iconSize: new L.Point(40, 40)
                        });
                      }}>
                      {groupedMapData[key].map((d, i) => (
                        <Marker
                          key={i} /* NOSONAR */
                          data={d}
                          category={category}
                          onPopupOpen={() => mapRef.current?.setMaxBounds()}
                          onPopupClose={() =>
                            mapRef.current?.setMaxBounds([westBound, northBound, eastBound, southBound])
                          }
                        />
                      ))}
                    </MarkerClusterGroup>
                  </LayerGroup>
                )
              );
            })}

            <div className={css.fullscreenToggle} onClick={onFullScreen} aria-hidden>
              {fullscreen && <FullscreenClose />}
              {!fullscreen && <FullscreenOpen />}
            </div>
            <ZoomControl position='topleft' zoomInText='' zoomOutText='' />
          </MapContainer>
        </div>
      </div>
    </main>
  );
}
